import { useState } from 'react';
import { addMonths, addYears, differenceInDays, format } from 'date-fns';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Button,
  Divider,
  Paper,
  TextField,
} from '@mui/material';
import { is } from '../../util/is';
import CloseIcon from '@mui/icons-material/Close';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';

const CounterInput = ({ value, onChange }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 1,
      overflow: 'hidden',
      width: 'fit-content',
    }}
  >
    <IconButton size="small" onClick={() => onChange(Math.max(0, value - 1))} sx={{ borderRadius: 0, px: 1 }}>
      <RemoveIcon fontSize="small" />
    </IconButton>
    <Typography sx={{ minWidth: 36, textAlign: 'center', fontSize: 14, fontWeight: 500 }}>
      {value}
    </Typography>
    <IconButton size="small" onClick={() => onChange(value + 1)} sx={{ borderRadius: 0, px: 1 }}>
      <AddIcon fontSize="small" />
    </IconButton>
  </Box>
);

const ReadonlyCount = ({ value }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 1,
      px: 2,
      py: 0.5,
      bgcolor: 'action.disabledBackground',
      width: 'fit-content',
    }}
  >
    <Typography sx={{ minWidth: 36, textAlign: 'center', fontSize: 14, fontWeight: 500, color: 'text.secondary' }}>
      {value}
    </Typography>
  </Box>
);

const SectionCard = ({ children, sx = {} }) => (
  <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, borderRadius: 2, ...sx }}>
    {children}
  </Paper>
);

const Row = ({ label, value, bold = false }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
    <Typography variant="body2" color={bold ? 'text.primary' : 'text.secondary'} fontWeight={bold ? 500 : 400}>
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={bold ? 500 : 400}>{value}</Typography>
  </Box>
);

/**
 * Props:
 * - open: boolean
 * - onClose: () => void
 * - teamName: string
 * - charger_license_payment_config: {
 *     pricing_model: 'M' | 'Y' | 'O',
 *     dc_charges_monthly: number,
 *     ac_charges_monthly: number,
 *     dc_charges_annual: number,
 *     ac_charges_annual: number,
 *     dc_charges_one_time: number,
 *     ac_charges_one_time: number,
 *     other_fixed_cost: number,
 *     trial_period: number,
 *     tax: number,
 *     payment_gateway_percent: number,
 *     currency: string,
 *     pg_tax: number,
 *   }
 * - isOfflinePayment: boolean           — shows payment ID field when true
 * - onConfirm: (payload) => void        — called on confirm, receives full order payload
 */
export const ChargerLicenseModal= ({
  open,
  onClose,
  teamName = 'Operations Team',
  charger_license_payment_config = {},
  isOfflinePayment = false,
  chargersToRenew,
  onConfirm,
}) => {
  const [acCount, setAcCount] = useState(0);
  const [dcCount, setDcCount] = useState(0);
  const [paymentId, setPaymentId] = useState('');
  const [paymentIdError, setPaymentIdError] = useState('');
  const [remarks, setRemarks] = useState('');

  const isRenewalMode = (chargersToRenew?.length ?? 0) > 0;
  const effectiveAcCount = isRenewalMode
    ? chargersToRenew.filter((c) => c.charger_type === 'AC').length
    : acCount;
  const effectiveDcCount = isRenewalMode
    ? chargersToRenew.filter((c) => c.charger_type === 'DC').length
    : dcCount;

  // Validate charger license payment config
  const isValidConfig = (config) => {
    const requiredFields = [
      'pricing_model', 'dc_charges_monthly', 'ac_charges_monthly',
      'dc_charges_annual', 'ac_charges_annual', 'other_fixed_cost',
      'tax',
      'payment_gateway_percent', 'currency', 'pg_tax'
    ];

    if (is.nullOrUndefined(config) || is.empty(config)) {
      return false;
    }

    return requiredFields.every(field => {
      const value = config[field];
      return !is.nullOrUndefined(value) &&
             !(is.string(value) && is.empty(value.trim()));
    });
  };

  const configValid = isValidConfig(charger_license_payment_config);

  // Extract values from config or use defaults
  const {
    pricing_model = 'M',
    dc_charges_monthly = 0,
    ac_charges_monthly = 0,
    dc_charges_annual = 0,
    ac_charges_annual = 0,
    dc_charges_one_time = 0,
    ac_charges_one_time = 0,
    other_fixed_cost = 0,
    tax = 0,
    payment_gateway_percent = 0,
    currency = 'USD',
    pg_tax = 0,
  } = configValid ? charger_license_payment_config : {};

  // Convert all numeric values to ensure proper calculations
  const numericConfig = {
    dc_charges_monthly: parseFloat(dc_charges_monthly) || 0,
    ac_charges_monthly: parseFloat(ac_charges_monthly) || 0,
    dc_charges_annual: parseFloat(dc_charges_annual) || 0,
    ac_charges_annual: parseFloat(ac_charges_annual) || 0,
    dc_charges_one_time: parseFloat(dc_charges_one_time) || 0,
    ac_charges_one_time: parseFloat(ac_charges_one_time) || 0,
    other_fixed_cost: parseFloat(other_fixed_cost) || 0,
    tax: parseFloat(tax) || 0,
    payment_gateway_percent: parseFloat(payment_gateway_percent) || 0,
    pg_tax: parseFloat(pg_tax) || 0,
  };

  // Calculate dynamic billing dates based on payment cycle
  const today = new Date();
  const formatBillingDate = (d) => format(d, 'dd MMM yyyy');

  // For active/inactive chargers nearing expiry, renewal starts from their final_expiry
  const nonExpiredChargers = isRenewalMode
    ? chargersToRenew.filter((c) => c.status !== 'EXPIRED' && c.final_expiry)
    : [];
  const renewsFromFinalExpiry = nonExpiredChargers.length > 0;
  const billingStartDate = renewsFromFinalExpiry
    ? new Date(Math.max(...nonExpiredChargers.map((c) => new Date(c.final_expiry))))
    : today;

  const nextBillingDate = pricing_model === 'M'
    ? addMonths(billingStartDate, 1)
    : pricing_model === 'Y'
    ? addYears(billingStartDate, 1)
    : null;

  const dynamicNextBillingDate = nextBillingDate ? formatBillingDate(nextBillingDate) : null;
  const dynamicDaysUntilBilling = nextBillingDate ? differenceInDays(nextBillingDate, today) : null;
  const dynamicBillingStartDate = renewsFromFinalExpiry ? formatBillingDate(billingStartDate) : null;

  const fmt = (amount) => `${currency} ${Number(amount).toFixed(2)}`;
  const billingLabel = pricing_model === 'M' ? 'month' : pricing_model === 'Y' ? 'year' : 'one-time';
  const billingCycleLabel = pricing_model === 'M' ? 'Monthly' : pricing_model === 'Y' ? 'Annual' : 'One-time';

  const acRate = pricing_model === 'M'
    ? numericConfig.ac_charges_monthly
    : pricing_model === 'Y'
    ? numericConfig.ac_charges_annual
    : numericConfig.ac_charges_one_time;
  const dcRate = pricing_model === 'M'
    ? numericConfig.dc_charges_monthly
    : pricing_model === 'Y'
    ? numericConfig.dc_charges_annual
    : numericConfig.dc_charges_one_time;

  const costMultiplier = pricing_model === 'Y' ? 12 : 1;
  const acCost = effectiveAcCount * acRate * costMultiplier;
  const dcCost = effectiveDcCount * dcRate * costMultiplier;
  const subtotal = acCost + dcCost + numericConfig.other_fixed_cost;

  const charger_tax_applicable = numericConfig.tax;
  const subtotalAfterTax = subtotal * (1 + charger_tax_applicable / 100);
  const taxAmount = subtotalAfterTax - subtotal;

  const pg_percent = numericConfig.payment_gateway_percent;
  const pg_gst_percent = numericConfig.pg_tax;
  const effective_pg_percent = parseFloat(
    (pg_percent * (1 + pg_gst_percent / 100)).toFixed(2),
  );

  const totalCost = subtotalAfterTax * (1 + effective_pg_percent / 100);
  const gatewayFee = totalCost - subtotalAfterTax;

  const handleConfirm = () => {
    if (isOfflinePayment && !paymentId.trim()) {
      setPaymentIdError('Payment ID is required for offline payments');
      return;
    }

    if (typeof onConfirm === 'function') {
      onConfirm({
        ac_count: effectiveAcCount,
        dc_count: effectiveDcCount,
        ac_charges: acRate,
        dc_charges: dcRate,
        subtotal,
        gateway_fee: gatewayFee,
        tax_amount: taxAmount,
        total_cost: parseFloat(totalCost.toFixed(2)),
        duration: pricing_model,
        currency,
        ...(isRenewalMode && { for_renewal: true }),
        ...(chargersToRenew?.length && { chargersToRenew }),
        ...(isOfflinePayment && { payment_id: paymentId.trim() }),
        ...(isOfflinePayment && remarks.trim() && { remarks: remarks.trim() }),
      });
    }
  };

  const handleClose = () => {
    setPaymentId('');
    setPaymentIdError('');
    setRemarks('');
    onClose();
  };

  // Show error state when config is invalid
  if (!configValid) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DescriptionOutlinedIcon fontSize="small" color="action" />
            <Typography variant="subtitle1" fontWeight={500}>
              {isRenewalMode ? 'Renew Charger Licenses' : 'Purchase Charger Licenses'}
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small" sx={{ position: 'absolute', top: 12, right: 12 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 0 }}>
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 200,
            textAlign: 'center'
          }}>
            <InfoOutlinedIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.primary" gutterBottom>
              Configuration Not Available
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 300 }}>
              Charger license finance config not present. Please contact your administrator to configure the payment settings.
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button variant="outlined" color="inherit" onClick={handleClose} fullWidth>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DescriptionOutlinedIcon fontSize="small" color="action" />
          <Typography variant="subtitle1" fontWeight={500}>
            Purchase Charger Licenses
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
          {isRenewalMode ? 'Renewing licenses for' : 'Add additional charger licenses for'}{' '}
          <Typography component="span" variant="caption" color="primary">
            {teamName}
          </Typography>
        </Typography>
        {isOfflinePayment && (
          <Typography variant="caption" sx={{ display: 'inline-block', mt: 0.5, px: 1, py: 0.25, bgcolor: 'action.selected', borderRadius: 1, color: 'text.secondary' }}>
            Offline payment
          </Typography>
        )}
        <IconButton onClick={handleClose} size="small" sx={{ position: 'absolute', top: 12, right: 12 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 0 }}>
        {/* Billing date */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, bgcolor: 'action.hover', borderRadius: 1, p: 1.5, mb: 1.5 }}>
          <CalendarTodayIcon fontSize="small" color="action" sx={{ mt: 0.2 }} />
          <Box>
            {renewsFromFinalExpiry && (
              <Typography variant="body2">
                <strong>Renewal starts:</strong> {dynamicBillingStartDate}
              </Typography>
            )}
            {pricing_model !== 'O' ? (
              <>
                <Typography variant="body2">
                  <strong>Next billing date:</strong> {dynamicNextBillingDate}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Billed {pricing_model === 'M' ? 'monthly' : 'annually'} — {dynamicDaysUntilBilling} days until next charge.
                </Typography>
              </>
            ) : (
              <Typography variant="caption" color="text.secondary">
                One-time payment — no recurring billing.
              </Typography>
            )}
          </Box>
        </Box>

        {/* Warning */}
        <Box sx={{ border: '1px solid', borderColor: 'warning.light', borderRadius: 2, p: 1.5, mb: 1.5, bgcolor: 'warning.50' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
            <InfoOutlinedIcon fontSize="small" sx={{ color: 'warning.dark' }} />
            <Typography variant="body2" fontWeight={500} color="warning.dark">
              {billingCycleLabel} billing cycle
            </Typography>
          </Box>
          <Typography variant="caption" color="warning.dark" fontWeight={500} display="block">
            {renewsFromFinalExpiry
              ? `Renewal will begin on ${dynamicBillingStartDate} (current license expiry) and run for one ${billingLabel}.`
              : isRenewalMode
                ? 'Selected chargers will be renewed immediately and billed for the current cycle.'
                : 'New chargers will be added immediately and billed for the current cycle.'}
          </Typography>
          {pricing_model !== 'O' && (
            <Typography variant="caption" color="warning.dark">
              Next billing: {dynamicNextBillingDate} ({dynamicDaysUntilBilling} days)
            </Typography>
          )}
        </Box>

        {/* Selected charger details */}
        {isRenewalMode && (
          <SectionCard>
            <Typography variant="body2" fontWeight={500} mb={1}>
              Selected chargers ({chargersToRenew.length})
            </Typography>
            {chargersToRenew.map((c) => (
              <Box
                key={c.charge_box_pk}
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.5, '&:not(:last-child)': { borderBottom: '1px solid', borderColor: 'divider' } }}
              >
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 13 }}>
                  {c.chargebox_id}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {c.charger_type && (
                    <Typography
                      variant="caption"
                      sx={{
                        px: 1, py: 0.25, borderRadius: 1, fontWeight: 600,
                        bgcolor: c.charger_type === 'DC' ? 'primary.lighter' : 'success.lighter',
                        color: c.charger_type === 'DC' ? 'primary.dark' : 'success.dark',
                      }}
                    >
                      {c.charger_type}
                    </Typography>
                  )}
                  <Typography
                    variant="caption"
                    sx={{
                      px: 1, py: 0.25, borderRadius: 1, fontWeight: 500,
                      bgcolor: c.status === 'EXPIRED' ? 'error.lighter' : 'warning.lighter',
                      color: c.status === 'EXPIRED' ? 'error.dark' : 'warning.dark',
                    }}
                  >
                    {c.status}
                  </Typography>
                </Box>
              </Box>
            ))}
          </SectionCard>
        )}

        {/* AC licenses */}
        <SectionCard>
          <Typography variant="body2" fontWeight={500} mb={1.5}>AC charger licenses</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {isRenewalMode
              ? <ReadonlyCount value={effectiveAcCount} />
              : <CounterInput value={acCount} onChange={setAcCount} />}
            <Typography variant="body2" color="text.secondary">
              × {fmt(acRate)}{pricing_model === 'Y' ? ' × 12' : ''}/{billingLabel} = {fmt(acCost)}
            </Typography>
          </Box>
        </SectionCard>

        {/* DC licenses */}
        <SectionCard>
          <Typography variant="body2" fontWeight={500} mb={1.5}>DC charger licenses</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {isRenewalMode
              ? <ReadonlyCount value={effectiveDcCount} />
              : <CounterInput value={dcCount} onChange={setDcCount} />}
            <Typography variant="body2" color="text.secondary">
              × {fmt(dcRate)}{pricing_model === 'Y' ? ' × 12' : ''}/{billingLabel} = {fmt(dcCost)}
            </Typography>
          </Box>
        </SectionCard>

        {/* Cost summary */}
        <SectionCard>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
            <DescriptionOutlinedIcon fontSize="small" color="action" />
            <Typography variant="body2" fontWeight={500}>Cost summary</Typography>
          </Box>

          {/* Base charges */}
          <Row label={`AC Chargers (${effectiveAcCount})`} value={fmt(acCost)} />
          <Row label={`DC Chargers (${effectiveDcCount})`} value={fmt(dcCost)} />
          {numericConfig.other_fixed_cost > 0 && <Row label="Other fixed cost" value={fmt(numericConfig.other_fixed_cost)} />}
          <Row label="Subtotal" value={fmt(subtotal)} />

          {/* Tax (GST/VAT) on base subtotal */}
          {numericConfig.tax > 0 && <Row label={`Tax (${numericConfig.tax}%)`} value={fmt(taxAmount)} />}
          {numericConfig.tax > 0 && <Row label="Subtotal after tax" value={fmt(subtotalAfterTax)} />}

          {/* Gateway charges on subtotal+tax */}
          {numericConfig.payment_gateway_percent > 0 && (
            <>
              <Row label={`Payment gateway fee incl. taxes (${effective_pg_percent}%)`} value={fmt(gatewayFee)} />
            </>
          )}

          <Divider sx={{ my: 1.5 }} />
          <Row label={`Total ${billingCycleLabel.toLowerCase()} cost`} value={fmt(totalCost)} bold />
        </SectionCard>

        {/* Offline payment ID + remarks — only shown when isOfflinePayment is true */}
        {isOfflinePayment && (
          <SectionCard sx={{ mb: 0, border: '1px solid', borderColor: paymentIdError ? 'error.light' : 'divider' }}>
            <Typography variant="body2" fontWeight={500} mb={1}>
              Payment reference
            </Typography>
            <TextField
              fullWidth
              size="small"
              label="Payment ID"
              placeholder="Enter offline payment ID"
              value={paymentId}
              onChange={(e) => {
                setPaymentId(e.target.value);
                if (e.target.value.trim()) setPaymentIdError('');
              }}
              error={!!paymentIdError}
              helperText={paymentIdError || 'Enter the reference ID from the offline transaction'}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              size="small"
              label="Remarks"
              placeholder="Add any notes about this purchase (optional)"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              multiline
              rows={3}
              inputProps={{ maxLength: 500 }}
              helperText={`${remarks.length}/500`}
              FormHelperTextProps={{ sx: { textAlign: 'right', mx: 0 } }}
            />
          </SectionCard>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button variant="outlined" color="inherit" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disableElevation
          onClick={handleConfirm}
          disabled={(effectiveAcCount === 0 && effectiveDcCount === 0) || (isOfflinePayment && !paymentId.trim())}
        >
          {isOfflinePayment
            ? 'Confirm offline payment'
            : isRenewalMode
              ? `Renew for ${fmt(totalCost)}${pricing_model !== 'O' ? `/${billingLabel}` : ''}`
              : `Purchase for ${fmt(totalCost)}${pricing_model !== 'O' ? `/${billingLabel}` : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
